import { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import { Check, Loader2, AlertTriangle, ArrowLeft } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const MAX_POLLS = 8;

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const sessionId = params.get("session_id");
  const [state, setState] = useState("checking"); // checking | paid | pending | error
  const [record, setRecord] = useState(null);

  useEffect(() => {
    if (!sessionId) {
      setState("error");
      return;
    }
    let attempts = 0;
    let timer;
    const poll = async () => {
      try {
        const { data } = await axios.get(`${API}/payments/status/${sessionId}`);
        setRecord(data);
        if (data.payment_status === "paid") {
          setState("paid");
          return;
        }
        if (["failed", "expired"].includes(data.payment_status)) {
          setState("error");
          return;
        }
        attempts += 1;
        if (attempts >= MAX_POLLS) {
          setState("pending");
          return;
        }
        timer = setTimeout(poll, 2000);
      } catch (e) {
        setState("error");
      }
    };
    poll();
    return () => clearTimeout(timer);
  }, [sessionId]);

  return (
    <div className="relative flex min-h-screen items-center justify-center bg-black px-4">
      <div className="noise-overlay" />
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
        className="w-full max-w-xl border border-white/20 bg-[#050505] p-10 text-center md:p-16"
        data-testid="payment-success-page"
      >
        {state === "checking" && (
          <>
            <Loader2 className="mx-auto mb-8 h-12 w-12 animate-spin text-white" />
            <p className="label-caps">Confirming your donation…</p>
          </>
        )}

        {state === "paid" && (
          <>
            <div className="mx-auto mb-8 flex h-16 w-16 items-center justify-center border border-white bg-white">
              <Check className="h-8 w-8 text-black" strokeWidth={2.5} />
            </div>
            <p className="label-caps mb-4">Payment Confirmed</p>
            <h1 className="font-oswald text-4xl font-bold uppercase leading-none tracking-tighter text-white md:text-5xl">
              Go raibh maith agat
            </h1>
            <p className="mt-6 text-white/80">
              Thank you{record?.donor_name ? `, ${record.donor_name}` : ""}. Your €1,000 donation
              towards the Ball Wall is confirmed. Your plaque message has been reserved:
            </p>
            {record?.plaque_message && (
              <p className="mt-6 border border-white/20 p-4 font-oswald text-lg uppercase tracking-wide text-white">
                “{record.plaque_message}”
              </p>
            )}
          </>
        )}

        {state === "pending" && (
          <>
            <Loader2 className="mx-auto mb-8 h-12 w-12 animate-spin text-white/70" />
            <p className="label-caps mb-4">Still Processing</p>
            <p className="text-white/80">
              Your payment is taking a moment to confirm. You'll receive an email once it's
              complete — no need to pay again.
            </p>
          </>
        )}

        {state === "error" && (
          <>
            <AlertTriangle className="mx-auto mb-8 h-12 w-12 text-white/70" />
            <p className="label-caps mb-4">Something Went Wrong</p>
            <p className="text-white/80">
              We couldn't confirm this payment. If you were charged, please contact the treasurer.
            </p>
          </>
        )}

        <button
          data-testid="back-home-btn"
          onClick={() => navigate("/ballwall")}
          className="mt-10 inline-flex items-center gap-2 border border-white/25 px-6 py-3 font-oswald text-xs uppercase tracking-[0.2em] text-white transition-colors hover:bg-white hover:text-black"
        >
          <ArrowLeft className="h-4 w-4" /> Back to Campaign
        </button>
      </motion.div>
    </div>
  );
}
